import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  can,
  parseMentions,
  type Message,
  type MessageAttachment,
  type ChannelReceipts,
  type PublicUser,
} from '@itsappy/shared';
import { useQueryClient } from '@tanstack/react-query';
import {
  useChannels,
  useDeleteMessage,
  useMarkChannelRead,
  useMemberUsers,
  useMessages,
  useMessageReceipts,
  useSendMessage,
  useToggleReaction,
} from '@/hooks/queries';
import { useAuth } from '@/store/auth';
import { toast } from '@/store/ui';
import { useSocket, useSocketEvent } from '@/realtime/socketContext';
import { Avatar, EmptyState, Modal, Spinner } from '@/components/ui';
import {
  ArrowBackIcon,
  CameraIcon,
  CallIcon,
  ChannelIcon,
  ChatIcon,
  CloseIcon,
  CopyIcon,
  EmojiIcon,
  ExpandIcon,
  NewMessageIcon,
  PeopleIcon,
  SpacesIcon,
  TrashIcon,
} from '@/components/ui/icons';
import { presenceLabel } from '@/components/ui/presence';
import { cn, messageTime, renderMentions } from '@/lib/utils';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useDmPeer } from '@/hooks/useDmPeer';
import { ChatAttachments, ImageGalleryProvider } from '@/components/chat/ChatMedia';
import { ChatComposer } from '@/components/chat/ChatComposer';
import {
  MessageReceipt,
  receiptsByMessage,
  type SeenEntry,
} from '@/components/chat/MessageReceipt';
import { ChannelInviteModal } from '@/components/chat/ChannelInviteModal';
import { EmojiPicker } from '@/components/chat/EmojiPicker';
import { AnchoredPopover, ReactionChips } from '@/components/chat/Reactions';
import { OutsideMentionDialog } from '@/components/chat/MessageDialogs';
import { MessageBody } from '@/components/chat/MessageBody';
import { useStartCall } from '@/hooks/useStartCall';
import { useChatAutoScroll } from '@/hooks/useChatAutoScroll';
import { copyText } from '@/lib/clipboard';
import { toComposerText, toMessageBody } from '@/lib/composerText';
import { QUICK_REACTIONS } from '@/lib/emoji';

export function ChatModal({
  onClose,
  initialChannelId,
  spaceId,
}: {
  onClose: () => void;
  initialChannelId?: string;
  /**
   * Set while the reader is standing on a floor.
   *
   * Chat opened from inside a space is about the space: the floor's own
   * conversation and the rooms on it, and nothing else. Handing someone the
   * whole workspace's inbox mid-meeting is how you end up reading a DM instead
   * of the room you are standing in — and the full chat is one click away.
   */
  spaceId?: string;
}) {
  const { user, role } = useAuth();
  const { socket } = useSocket();
  const showsTwoPanes = useMediaQuery('(min-width: 640px)');
  const queryClient = useQueryClient();
  const { data: channels, isLoading: channelsLoading } = useChannels();
  const members = useMemberUsers();
  const [channelId, setChannelId] = useState<string | undefined>(initialChannelId);
  const [draft, setDraft] = useState('');
  const [attachments, setAttachments] = useState<MessageAttachment[]>([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  /** Non-null while the caret sits in an `@word`: the text typed after the @. */
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionCursor, setMentionCursor] = useState(0);
  /** A send held back because it mentions people outside this conversation. */
  const [outsideMentions, setOutsideMentions] = useState<{
    users: PublicUser[];
    extra: MessageAttachment[];
    body: string;
  } | null>(null);
  /**
   * The message whose reaction picker is open, held here rather than in the
   * row so only one can be open, and so Escape closes the picker instead of
   * the whole chat.
   */
  const [pickerFor, setPickerFor] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  /**
   * Set when Escape dismissed the mention list. The composer only calls
   * `preventDefault`, so the same keypress still reaches the window listener
   * below — which would otherwise close the whole chat and lose the draft.
   */
  const escapeHandledRef = useRef(false);
  /** The conversation side of the modal, so a file can be dropped anywhere in it. */
  const paneRef = useRef<HTMLDivElement>(null);
  const initializedRef = useRef(Boolean(initialChannelId));
  const readTimerRef = useRef<number | null>(null);

  const channel = channels?.items.find((item) => item.id === channelId);
  const dmPeer = useDmPeer(channel);
  const names = useMemo(
    () => new Map(members.map((member) => [member.id, member.name])),
    [members],
  );
  const canInvite = Boolean(
    channel?.kind === 'channel' &&
    channel.isPrivate &&
    user &&
    (channel.createdBy === user.id || (role && can(role, 'message:moderate'))),
  );
  const { data: messages, isLoading: messagesLoading } = useMessages(channelId);
  const { data: receiptData } = useMessageReceipts(channelId);
  /* Off unless the server says this conversation has receipts — a big room
     and a reader who turned theirs off both come back `disabled`. */
  const receiptVariant: 'dm' | 'group' | 'off' =
    !receiptData || receiptData.disabled ? 'off' : channel?.kind === 'dm' ? 'dm' : 'group';

  const seenByMessage = useMemo(() => {
    const people = new Map<string, PublicUser>();
    for (const person of [...members, ...(channel?.peers ?? [])]) people.set(person.id, person);
    return receiptsByMessage(
      receiptVariant === 'off' ? undefined : messages?.items,
      receiptData?.items,
      user?.id,
      people,
    );
  }, [members, channel?.peers, receiptVariant, messages?.items, receiptData?.items, user?.id]);

  /** The newest message you sent here — the only one that says "Sent". */
  const lastOwnMessageId = useMemo(() => {
    const items = messages?.items ?? [];
    for (let index = items.length - 1; index >= 0; index -= 1) {
      if (items[index].author.id === user?.id) return items[index].id;
    }
    return undefined;
  }, [messages?.items, user?.id]);
  const send = useSendMessage(channelId ?? '');
  const deleteMessage = useDeleteMessage(channelId ?? '');
  const markRead = useMarkChannelRead();
  const { mutate: mutateReaction } = useToggleReaction(channelId ?? '');
  const [deletingMessage, setDeletingMessage] = useState<Message | null>(null);
  const { startCall, starting: callStarting, connected: callConnected } = useStartCall(onClose);
  const requestMessageDelete = useCallback(
    (message: Message) => setDeletingMessage(message),
    [],
  );
  const reactToMessage = useCallback(
    (messageId: string, emoji: string) => mutateReaction({ messageId, emoji }),
    [mutateReaction],
  );
  const copyMessage = useCallback(
    async (message: Message) => {
      try {
        await copyText(renderMentions(message.body, names));
        toast.success('Message copied');
      } catch {
        toast.error('Could not copy message');
      }
    },
    [names],
  );

  useEffect(() => {
    if (initializedRef.current || !showsTwoPanes || !channels?.items.length) return;
    const initial = channels.items.find((item) => item.unreadCount > 0) ?? channels.items[0];
    initializedRef.current = true;
    setChannelId(initial.id);
  }, [channels, showsTwoPanes]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (escapeHandledRef.current) {
        escapeHandledRef.current = false;
        return;
      }
      /* With a reaction picker open, Escape means that picker, not the chat. */
      if (pickerFor) {
        setPickerFor(null);
        return;
      }
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, pickerFor]);

  useEffect(() => {
    if (!socket || !channelId) return;
    if (readTimerRef.current) window.clearTimeout(readTimerRef.current);
    setAttachments([]);
    setMentionQuery(null);
    setPickerFor(null);
    socket.emit('channel:subscribe', { channelId });
    markRead.mutate(channelId);
    return () => {
      if (readTimerRef.current) window.clearTimeout(readTimerRef.current);
      socket.emit('channel:unsubscribe', { channelId });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, channelId]);

  useSocketEvent('message:new', ({ message }) => {
    if (message.channelId !== channelId || message.parentId) return;
    queryClient.setQueryData<{ items: Message[] }>(['messages', channelId], (current) =>
      current && !current.items.some((item) => item.id === message.id)
        ? { ...current, items: [...current.items, message] }
        : current,
    );
    if (message.author.id !== user?.id) queueReadReceipt(channelId);
  });

  useSocketEvent('message:updated', ({ message }) => {
    queryClient.setQueryData<{ items: Message[] }>(
      ['messages', message.channelId],
      (current) =>
        current
          ? {
              ...current,
              items: current.items.map((item) => (item.id === message.id ? message : item)),
            }
          : current,
    );
  });

  useSocketEvent('message:translated', ({ channelId: id, messageId, sourceLang, translations }) => {
    queryClient.setQueryData<{ items: Message[] }>(['messages', id], (current) =>
      current
        ? {
            ...current,
            items: current.items.map((item) =>
              item.id === messageId ? { ...item, sourceLang, translations } : item,
            ),
          }
        : current,
    );
  });

  /* Same rule as the full chat page: one row per reader, and a cursor never
     moves backwards because an event arrived late. */
  useSocketEvent('channel:seen', ({ channelId: readChannelId, userId, lastReadAt }) => {
    queryClient.setQueryData<ChannelReceipts>(['message-receipts', readChannelId], (current) => {
      if (!current || current.disabled) return current;
      const previous = current.items.find((item) => item.userId === userId);
      if (previous && previous.readAt > lastReadAt) return current;
      return {
        ...current,
        items: [
          ...current.items.filter((item) => item.userId !== userId),
          { userId, readAt: lastReadAt },
        ],
      };
    });
  });

  useSocketEvent('message:deleted', ({ channelId: id, messageId }) => {
    queryClient.setQueryData<{ items: Message[] }>(['messages', id], (current) =>
      current
        ? { ...current, items: current.items.filter((item) => item.id !== messageId) }
        : current,
    );
  });

  useChatAutoScroll({
    containerRef: scrollRef,
    contentRef,
    identity: channelId,
    itemCount: messages?.items.length ?? 0,
  });

  function queueReadReceipt(id: string) {
    if (readTimerRef.current) window.clearTimeout(readTimerRef.current);
    readTimerRef.current = window.setTimeout(() => markRead.mutate(id), 300);
  }

  /* On a floor, the conversation list is that floor's. Everything below reads
     this rather than the raw list, so the unread count in the header counts
     what is actually on screen. */
  const inScope = useMemo(
    () =>
      (channels?.items ?? []).filter((item) => !spaceId || item.spaceId === spaceId),
    [channels, spaceId],
  );
  const totalUnread = useMemo(
    () => inScope.reduce((total, item) => total + item.unreadCount, 0),
    [inScope],
  );
  /** The floor's own conversation, which is the one with no room behind it. */
  const floorChannel = useMemo(
    () => (spaceId ? inScope.find((item) => !item.zoneId) : undefined),
    [inScope, spaceId],
  );
  const conversationGroups = useMemo(
    () =>
      (spaceId
        ? [
            { label: 'This floor', items: inScope.filter((item) => !item.zoneId) },
            { label: 'Rooms', items: inScope.filter((item) => Boolean(item.zoneId)) },
          ]
        : [
            { label: 'Direct messages', items: inScope.filter((item) => item.kind === 'dm') },
            { label: 'Channels', items: inScope.filter((item) => item.kind === 'channel') },
            { label: 'Spaces', items: inScope.filter((item) => item.kind === 'room') },
          ]
      ).filter((group) => group.items.length > 0),
    [inScope, spaceId],
  );

  /**
   * `extra` carries an attachment that should go out immediately — a finished
   * voice note — rather than waiting in the composer for a second Send.
   */
  /* `body` overrides the draft: a caption typed over an attachment preview
     never lived in the composer, so it travels with the send. */
  function submit(
    extra: MessageAttachment[] = [],
    {
      body: bodyOverride,
      ignoreOutsideMentions,
    }: { body?: string; ignoreOutsideMentions?: boolean } = {},
  ) {
    /* The composer shows `@Name`; the message stores `<@id>`. Without this the
       mention went out as plain text — a chip that never rendered and a badge
       that never arrived. */
    const body = toMessageBody((bodyOverride ?? draft).trim(), members);
    const pendingAttachments = [...attachments, ...extra];
    if ((!body && pendingAttachments.length === 0) || !channelId) return;

    /* In a DM or private channel the server only badges members, so mentioning
       anyone else reaches no one. Hold the send and say so, as the full chat
       does — the author can add them (private channel) or learn why not (DM). */
    if (!ignoreOutsideMentions && channel && (channel.kind === 'dm' || channel.isPrivate)) {
      const outside = parseMentions(body)
        .filter((id) => !channel.memberIds.includes(id))
        .map((id) => members.find((member) => member.id === id))
        .filter((member): member is PublicUser => Boolean(member));
      if (outside.length > 0) {
        setOutsideMentions({ users: outside, extra, body });
        return;
      }
    }

    setDraft('');
    setAttachments([]);
    setMentionQuery(null);
    send.mutate(
      { body, attachments: pendingAttachments },
      {
        onError: (error) => {
          setDraft(toComposerText(body, names));
          setAttachments(pendingAttachments);
          toast.error('Message not sent', error instanceof Error ? error.message : undefined);
        },
      },
    );
  }

  function onDraftChange(value: string) {
    setDraft(value);
    /* The word the caret is in, not the end of the box, so a mention can be
       added back into a sentence already written. */
    const caret = inputRef.current?.selectionStart ?? value.length;
    const match = /(?:^|\s)@(\w*)$/.exec(value.slice(0, caret));
    setMentionQuery(match ? match[1].toLowerCase() : null);
    setMentionCursor(0);
  }

  function insertMention(member: PublicUser) {
    const input = inputRef.current;
    const caret = input?.selectionStart ?? draft.length;
    /* The name, not the id — `submit` puts the `<@id>` token back. */
    const head = draft.slice(0, caret).replace(/@\w*$/, `@${member.name} `);
    setDraft(head + draft.slice(caret));
    setMentionQuery(null);
    requestAnimationFrame(() => {
      input?.focus();
      input?.setSelectionRange(head.length, head.length);
    });
  }

  const mentionMatches = useMemo(
    () =>
      mentionQuery === null
        ? []
        : members
            .filter(
              (member) =>
                member.name.toLowerCase().includes(mentionQuery) ||
                (member.title ?? '').toLowerCase().includes(mentionQuery),
            )
            .slice(0, 6),
    [mentionQuery, members],
  );
  const activeMention = mentionMatches[Math.min(mentionCursor, mentionMatches.length - 1)];

  /** Keys the suggestion list claims before the composer acts on them. */
  function interceptComposerKey(event: React.KeyboardEvent<HTMLTextAreaElement>): boolean {
    if (mentionMatches.length === 0) return false;
    if (event.key === 'ArrowDown') {
      setMentionCursor((cursor) => (cursor + 1) % mentionMatches.length);
      return true;
    }
    if (event.key === 'ArrowUp') {
      setMentionCursor(
        (cursor) => (cursor - 1 + mentionMatches.length) % mentionMatches.length,
      );
      return true;
    }
    if ((event.key === 'Enter' && !event.shiftKey) || event.key === 'Tab') {
      if (activeMention) insertMention(activeMention);
      return true;
    }
    if (event.key === 'Escape') {
      setMentionQuery(null);
      escapeHandledRef.current = true;
      return true;
    }
    return false;
  }

  return (
    <div
      className="fixed inset-0 z-[55] p-2 sm:p-0"
      role="dialog"
      aria-modal="true"
      aria-label="Chat"
    >
      <button
        type="button"
        aria-label="Close chat"
        onClick={onClose}
        className="absolute inset-0 bg-black/45"
      />
      <section className="relative mx-auto flex h-[calc(100dvh-1rem)] w-full max-w-[46rem] overflow-hidden rounded-2xl bg-surface-1 shadow-2xl ring-1 ring-surface-3 sm:absolute sm:bottom-[calc(5.25rem+env(safe-area-inset-bottom))] sm:right-5 sm:h-[min(42rem,calc(100dvh-7rem))] sm:w-[min(46rem,calc(100vw-2.5rem))]">
        <aside
          className={`${channelId ? 'hidden sm:flex' : 'flex'} w-full shrink-0 flex-col border-r border-surface-2 bg-surface-1 sm:w-52`}
        >
          <header className="flex min-h-14 shrink-0 items-center gap-2 border-b border-surface-2 px-3 pt-[env(safe-area-inset-top)]">
            <div className="min-w-0 flex-1">
              <h2 className="truncate text-sm font-semibold text-ink">
                {spaceId ? (floorChannel?.name ?? 'This floor') : 'Chat'}
              </h2>
              <p className="text-xs text-slate-500">
                {totalUnread > 0
                  ? `${totalUnread} unread`
                  : spaceId
                    ? 'This floor and its rooms'
                    : 'All caught up'}
              </p>
            </div>
            {/* On a floor the way out is the whole chat, not a new DM: leaving
                the space is a decision, so it is spelled out rather than
                hidden behind a compose icon. */}
            <Link
              to="/chat"
              onClick={onClose}
              title={spaceId ? 'Open the full chat' : 'New direct message or group'}
              aria-label={spaceId ? 'Open the full chat' : 'New direct message or group'}
              className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 hover:bg-surface-2 hover:text-ink"
            >
              {spaceId ? <ExpandIcon /> : <NewMessageIcon />}
            </Link>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close chat"
              className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 hover:bg-surface-2 hover:text-ink sm:hidden"
            >
              <CloseIcon />
            </button>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
            {channelsLoading ? (
              <div className="flex justify-center py-8">
                <Spinner />
              </div>
            ) : !inScope.length ? (
              <EmptyState
                icon={ChatIcon}
                title={spaceId ? 'Nothing said here yet' : 'No conversations'}
                description={
                  spaceId
                    ? 'Walk into a room to open its conversation.'
                    : undefined
                }
              />
            ) : (
              conversationGroups.map((group, groupIndex) => (
                <Fragment key={group.label}>
                  <p
                    className={`px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500 ${groupIndex ? 'pt-3' : 'pt-1'}`}
                  >
                    {group.label}
                  </p>
                  {group.items.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setChannelId(item.id)}
                      className={`flex min-h-[44px] w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm transition-colors ${
                        item.id === channelId
                          ? item.kind === 'room'
                            ? 'bg-brand-600/15 text-brand-200'
                            : 'bg-surface-3 text-ink'
                          : 'text-slate-400 hover:bg-surface-2 hover:text-ink'
                      }`}
                    >
                      {item.kind === 'dm' && item.peers[0] ? (
                        <Avatar user={item.peers[0]} size="xs" showStatus />
                      ) : (
                        <span
                          className={`grid w-5 place-items-center ${item.kind === 'room' ? 'text-brand-300' : 'text-slate-500'}`}
                        >
                          {item.kind === 'room' ? <SpacesIcon /> : <ChannelIcon />}
                        </span>
                      )}
                      <span className="min-w-0 flex-1 truncate">{item.name}</span>
                      {item.unreadCount > 0 && (
                        <span className="rounded-full bg-brand-600 px-1.5 text-[10px] font-semibold text-white">
                          {item.unreadCount > 99 ? '99+' : item.unreadCount}
                        </span>
                      )}
                    </button>
                  ))}
                </Fragment>
              ))
            )}
          </div>
        </aside>

        <div
          ref={paneRef}
          className={`${channelId ? 'flex' : 'hidden sm:flex'} relative min-w-0 flex-1 flex-col`}
        >
          <header className="flex min-h-14 shrink-0 items-center gap-1 border-b border-surface-2 px-2 pt-[env(safe-area-inset-top)] sm:gap-2 sm:px-4 sm:pt-0">
            <button
              type="button"
              onClick={() => setChannelId(undefined)}
              aria-label="Back to conversations"
              className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 hover:bg-surface-2 hover:text-ink sm:hidden"
            >
              <ArrowBackIcon />
            </button>
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 items-center gap-2">
                {channel?.kind === 'room' && (
                  <SpacesIcon size="sm" className="text-brand-300" />
                )}
                <h3 className="truncate text-sm font-semibold text-ink">
                  {channel?.kind === 'dm' || channel?.kind === 'room'
                    ? channel?.name
                    : `#${channel?.name ?? ''}`}
                </h3>
                {channel?.kind === 'room' && (
                  <span className="shrink-0 rounded-md bg-brand-600/15 px-1.5 py-0.5 text-[10px] font-semibold text-brand-200">
                    Space chat
                  </span>
                )}
              </div>
              {dmPeer ? (
                <p className="truncate text-[11px] leading-tight text-slate-500">
                  {presenceLabel(dmPeer)}
                </p>
              ) : (
                channel?.topic && (
                  <p className="truncate text-xs text-slate-500">{channel.topic}</p>
                )
              )}
            </div>
            {canInvite && (
              <button
                type="button"
                onClick={() => setInviteOpen(true)}
                aria-label={`Invite people to #${channel?.name}`}
                title="Invite people"
                className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 hover:bg-surface-2 hover:text-ink"
              >
                <PeopleIcon />
              </button>
            )}
            {channelId && (
              <button
                type="button"
                onClick={() => startCall(channelId, { video: true })}
                disabled={callStarting || !callConnected}
                aria-label={`Start video call in ${channel?.name ?? 'conversation'}`}
                title={callConnected ? 'Start video call' : 'Call reconnecting'}
                className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 hover:bg-surface-2 hover:text-ink disabled:cursor-not-allowed disabled:opacity-40"
              >
                <CameraIcon />
              </button>
            )}
            {channelId && (
              <button
                type="button"
                onClick={() => startCall(channelId)}
                disabled={callStarting || !callConnected}
                aria-label={`Start call in ${channel?.name ?? 'conversation'}`}
                title={callConnected ? 'Start voice call' : 'Call reconnecting'}
                className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 hover:bg-surface-2 hover:text-ink disabled:cursor-not-allowed disabled:opacity-40"
              >
                {callStarting ? <Spinner className="h-4 w-4" /> : <CallIcon />}
              </button>
            )}
            {channelId && (
              <Link
                to={`/chat/${channelId}`}
                onClick={onClose}
                aria-label="Open full chat"
                title="Open full chat"
                className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 hover:bg-surface-2 hover:text-ink"
              >
                <ExpandIcon />
              </Link>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close chat"
              className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 hover:bg-surface-2 hover:text-ink"
            >
              <CloseIcon />
            </button>
          </header>

          <ImageGalleryProvider messages={messages?.items}>
            <div
              ref={scrollRef}
              className="min-h-0 flex-1 overscroll-contain overflow-y-auto px-2 py-2 sm:px-4 sm:py-2.5"
            >
              {messagesLoading ? (
                <div className="flex justify-center py-12">
                  <Spinner />
                </div>
              ) : !messages?.items.length ? (
                <EmptyState
                  icon={ChatIcon}
                  title="No messages yet"
                  description={
                    channel?.kind === 'room'
                      ? 'Start a conversation with everyone in this space.'
                      : 'Start the conversation.'
                  }
                />
              ) : (
                <div ref={contentRef} className="space-y-1">
                  {messages.items.map((message) => (
                    <ModalMessageRow
                      key={message.id}
                      message={message}
                      viewerId={user?.id}
                      seen={seenByMessage.get(message.id)}
                      isLastOwn={message.id === lastOwnMessageId}
                      receiptVariant={receiptVariant}
                      names={names}
                      canDelete={Boolean(
                        message.author.id === user?.id ||
                        (channel?.kind !== 'dm' && role && can(role, 'message:moderate')),
                      )}
                      onDelete={requestMessageDelete}
                      onCopy={copyMessage}
                      onReact={reactToMessage}
                      pickerOpen={pickerFor === message.id}
                      onPickerOpenChange={setPickerFor}
                    />
                  ))}
                </div>
              )}
            </div>
          </ImageGalleryProvider>

          {channel && (
            <footer className="relative shrink-0 border-t border-surface-2 bg-surface-1 p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:p-2.5">
              {mentionMatches.length > 0 && (
                <div
                  role="listbox"
                  aria-label="Mention someone"
                  className="absolute bottom-full left-2 mb-1 w-56 overflow-hidden rounded-xl border border-surface-3 bg-surface-2 shadow-2xl"
                >
                  {mentionMatches.map((member, index) => (
                    <button
                      key={member.id}
                      type="button"
                      role="option"
                      aria-selected={member.id === activeMention?.id}
                      onMouseEnter={() => setMentionCursor(index)}
                      onClick={() => insertMention(member)}
                      className={cn(
                        'flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-200',
                        member.id === activeMention?.id
                          ? 'bg-surface-3'
                          : 'hover:bg-surface-3',
                      )}
                    >
                      <Avatar user={member} size="xs" />
                      {member.name}
                    </button>
                  ))}
                </div>
              )}
              <ChatComposer
                value={draft}
                onChange={onDraftChange}
                onSubmit={submit}
                attachments={attachments}
                onAttachmentsChange={setAttachments}
                placeholder={`Message ${channel.kind === 'channel' ? `#${channel.name}` : channel.name}`}
                disabled={send.isPending}
                inputRef={inputRef}
                interceptKeyDown={interceptComposerKey}
                dropZoneRef={paneRef}
              />
            </footer>
          )}
        </div>
      </section>
      <Modal
        open={Boolean(deletingMessage)}
        onClose={() => setDeletingMessage(null)}
        title="Delete message for everyone?"
        description="This message and its attachments will disappear from the conversation for everyone. This cannot be undone."
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setDeletingMessage(null)}
            >
              Keep message
            </button>
            <button
              type="button"
              className="btn-danger"
              disabled={deleteMessage.isPending}
              onClick={async () => {
                if (!deletingMessage) return;
                try {
                  await deleteMessage.mutateAsync(deletingMessage.id);
                  setDeletingMessage(null);
                  toast.success('Message deleted for everyone');
                } catch (error) {
                  toast.error(
                    'Could not delete message',
                    error instanceof Error ? error.message : undefined,
                  );
                }
              }}
            >
              {deleteMessage.isPending && <Spinner />}
              Delete for everyone
            </button>
          </>
        }
      >
        <p className="text-sm text-slate-400">
          Only delete messages you no longer want anyone in this conversation to access.
        </p>
      </Modal>
      {channel && (
        <ChannelInviteModal
          channel={channel}
          members={members}
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
        />
      )}
      {channel && outsideMentions && (
        <OutsideMentionDialog
          users={outsideMentions.users}
          channel={channel}
          canInvite={canInvite}
          onClose={() => setOutsideMentions(null)}
          onProceed={() => {
            const held = outsideMentions;
            setOutsideMentions(null);
            submit(held.extra, { body: held.body, ignoreOutsideMentions: true });
          }}
        />
      )}
    </div>
  );
}

const ModalMessageRow = memo(function ModalMessageRow({
  message,
  viewerId,
  seen,
  isLastOwn,
  receiptVariant,
  names,
  canDelete,
  onDelete,
  onCopy,
  onReact,
  pickerOpen,
  onPickerOpenChange,
}: {
  message: Message;
  viewerId?: string;
  /** Who has read this far, when anyone has. */
  seen?: SeenEntry;
  /** The last message this reader sent here. */
  isLastOwn: boolean;
  receiptVariant: 'dm' | 'group' | 'off';
  names: Map<string, string>;
  canDelete: boolean;
  onDelete: (message: Message) => void;
  onCopy: (message: Message) => void;
  onReact: (messageId: string, emoji: string) => void;
  pickerOpen: boolean;
  /** Takes the message id to open its picker, or null to close whichever is. */
  onPickerOpenChange: (messageId: string | null) => void;
}) {
  const isSelf = message.author.id === viewerId;
  /* Two steps behind one button: the six quick reactions, and the full picker
     for anything else. The bubble is too narrow to carry a hover bar. */
  const [browsing, setBrowsing] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const closePicker = useCallback(() => {
    onPickerOpenChange(null);
    setBrowsing(false);
  }, [onPickerOpenChange]);

  function react(emoji: string) {
    onReact(message.id, emoji);
    closePicker();
  }

  return (
    <article
      className={`group flex items-end gap-2 px-1.5 py-1.5 sm:gap-2.5 sm:px-2 ${isSelf ? 'flex-row-reverse' : ''}`}
    >
      <Avatar user={message.author} size="xs" showStatus />
      <div
        className={`min-w-0 max-w-[78%] break-words rounded-2xl px-3 py-2 shadow-sm ring-1 ring-inset ${
          isSelf
            ? 'rounded-br-md bg-brand-600/15 ring-brand-500/20'
            : 'rounded-bl-md bg-surface-2 ring-surface-3'
        }`}
      >
        <p
          className={`flex min-w-0 items-baseline gap-1.5 text-xs ${isSelf ? 'justify-end' : ''}`}
        >
          <span className={`font-semibold ${isSelf ? 'text-brand-200' : 'text-slate-100'}`}>
            {isSelf ? 'You' : message.author.name}
          </span>
          <span className="shrink-0 text-slate-500">
            {message.id.startsWith('pending-') ? 'Sending…' : messageTime(message.createdAt)}
          </span>
          <MessageReceipt
            message={message}
            seen={seen}
            isLastOwn={isLastOwn}
            variant={receiptVariant}
          />
        </p>
        {message.body.trim() && (
          <MessageBody message={message} names={names} viewerId={viewerId} />
        )}
        <ChatAttachments attachments={message.attachments} />
        <ReactionChips
          message={message}
          viewerId={viewerId}
          onReact={onReact}
          className={isSelf ? 'justify-end' : undefined}
        />
      </div>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => onPickerOpenChange(pickerOpen ? null : message.id)}
        aria-label="Add reaction"
        title="Add reaction"
        aria-expanded={pickerOpen}
        className={cn(
          'grid h-10 w-10 shrink-0 place-items-center rounded-lg text-slate-400 hover:bg-surface-3 hover:text-ink sm:h-8 sm:w-8',
          !pickerOpen &&
            'sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100',
        )}
      >
        <EmojiIcon />
      </button>
      {pickerOpen && (
        /* Keyed: stepping up to the full picker is a fifteen-fold change in
           height, and a remount measures it there and then rather than waiting
           on the observer's next frame. */
        <AnchoredPopover
          key={browsing ? 'picker' : 'quick'}
          anchorRef={triggerRef}
          onClose={closePicker}
        >
          {browsing ? (
            <EmojiPicker onClose={closePicker} onPick={(pick) => react(pick.value)} />
          ) : (
            <div className="flex items-center gap-0.5 rounded-lg border border-surface-3 bg-surface-2 px-1 py-0.5 shadow-lg">
              {QUICK_REACTIONS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => react(emoji)}
                  aria-label={`React with ${emoji}`}
                  className="grid h-8 w-8 place-items-center rounded-md text-sm hover:bg-surface-3"
                >
                  {emoji}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setBrowsing(true)}
                aria-label="More reactions"
                title="More reactions"
                className="grid h-8 w-8 place-items-center rounded-md text-slate-400 hover:bg-surface-3 hover:text-ink"
              >
                <EmojiIcon size="sm" />
              </button>
            </div>
          )}
        </AnchoredPopover>
      )}
      {message.body.trim() && (
        <button
          type="button"
          onClick={() => onCopy(message)}
          aria-label="Copy message text"
          title="Copy message"
          className="grid h-10 w-10 shrink-0 place-items-center rounded-lg text-slate-400 hover:bg-surface-3 hover:text-ink sm:h-8 sm:w-8 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100"
        >
          <CopyIcon />
        </button>
      )}
      {canDelete && (
        <button
          type="button"
          onClick={() => onDelete(message)}
          aria-label="Delete message for everyone"
          title="Delete for everyone"
          className="grid h-10 w-10 shrink-0 place-items-center rounded-lg text-red-300 hover:bg-red-500/10 hover:text-red-200 sm:h-8 sm:w-8 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100"
        >
          <TrashIcon />
        </button>
      )}
    </article>
  );
});
